import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { FirmsService } from 'api';
import {
  COMPANY_LIST_BY_FIRM_DEFAULT_ERROR_MESSAGE,
  COMPANY_LIST_BY_FIRM_QUERY_KEY,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { FirmCompany } from 'common/types/company';
import { throwError } from 'utilities';
import {
  GetCompanyListByFirmParams,
  GetCompanyListByFirmResponse,
  GetCompanyListByFirmReturn,
  UseGetCompanyListByFirmParams,
} from './useGetCompanyListByFirm.types';

type CompanyListByFirmApiResponse = Awaited<ReturnType<typeof FirmsService.apiFirmsCompaniesList>>;

const normalizeCompanyListByFirmResponse = (
  response: CompanyListByFirmApiResponse | GetCompanyListByFirmResponse
): GetCompanyListByFirmResponse => {
  if (Array.isArray(response)) {
    return response;
  }

  // OpenAPI currently models this endpoint as paginated while app consumers expect the legacy flat array shape.
  return (response.results ?? []) as unknown as FirmCompany[];
};

export const getCompanyListByFirm = async (
  params: GetCompanyListByFirmParams
): Promise<GetCompanyListByFirmResponse | null> => {
  const { firmId } = params;

  if (isNil(firmId)) {
    return throwError(COMPANY_LIST_BY_FIRM_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const response = await FirmsService.apiFirmsCompaniesList(toString(firmId));

    if (!response) {
      return throwError(COMPANY_LIST_BY_FIRM_DEFAULT_ERROR_MESSAGE);
    }

    return normalizeCompanyListByFirmResponse(response);
  } catch (error) {
    return throwError(COMPANY_LIST_BY_FIRM_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetCompanyListByFirm = (params: UseGetCompanyListByFirmParams): GetCompanyListByFirmReturn => {
  const { firmId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch, fetchStatus } = useQuery<
    GetCompanyListByFirmReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && shouldQueryAutomatically,
    queryFn: () => getCompanyListByFirm({ firmId }),
    queryKey: [COMPANY_LIST_BY_FIRM_QUERY_KEY, firmId],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
    // Treat cached company lists as fresh for 5 minutes to prevent background
    // refetches from disabling the company chip on every firm navigation.
    staleTime: 5 * 60 * 1000,
  });

  // Keep in-flight cache entries out of the query-to-store bridge. The chip
  // continues using the existing store list while a background refetch runs.
  const successData = useMemo(() => {
    if (fetchStatus === 'idle') {
      return data;
    }

    return null;
  }, [data, fetchStatus]);

  return {
    data: successData,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetCompanyListByFirm;
